import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppHeader } from '../templates/AppHeader';
import { Button } from '../atoms/Button';
import { PageContent } from '../templates/PageContent';
import { LabelSection } from '../molecules/LabelSection';
import { LabeledInputField } from '../molecules/LabeledInputField';
import { Form } from '../templates/Form';
import LoadingModal from '../organisms/LoadingModal';
import { NotificationReceiver, TwoFactorAuthenticationReceiver, User } from '../domains/User';
import { PhoneNumber } from '../domains/PhoneNumber';
import { LabeledRadioButtonGroup } from '../molecules/LabeledRadioButtonGroup';
import MessageModal from 'organisms/MessageModal';
import { AppFooter } from 'templates/AppFooter';
import { AppContext } from 'templates/AppContextProvider';
import { useGetUser } from 'hooks/u006/useGetUserHooks';
import { useUpdateUser } from 'hooks/u006/useUpdateUserHooks';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { useEditUserFormSchema } from 'hooks/u006/useEditUserFormSchema';
import { EditUserFormSchemaType } from 'commons/userEdit';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { TransitionButton } from 'atoms/TransitionButton';
import { SelectCountryNumber } from 'atoms/SelectCountryNumber';
import { Country, getCountryCallingCode } from 'react-phone-number-input/input';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useDeleteLineAccountLink } from 'hooks/u013/useDeleteLineAccountLink';
import { LabelLineAddFriendField } from 'molecules/LabelLineAddFriendField';
import { LineUnLinkDialog } from 'organisms/LineUnLinkDialog';

let revision: number;

/**
 * ユーザオブジェクトの比較を行う関数
 * @param obj1 - 比較対象のオブジェクト1
 * @param obj2 - 比較対象のオブジェクト2
 * @returns オブジェクトが等しい場合はtrue、それ以外はfalse
 */
function deepEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) return true;
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }
  if (obj1 instanceof PhoneNumber && obj2 instanceof PhoneNumber) {
    return obj1.value === obj2.value;
  }
  if ((obj1 === null && obj2 === undefined) || (obj1 === undefined && obj2 === null)) {
    return true;
  }
  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
    return false;
  }
  const obj1AsObject = obj1 as Record<string, unknown>;
  const obj2AsObject = obj2 as Record<string, unknown>;
  const keys1 = Object.keys(obj1AsObject);
  const keys2 = Object.keys(obj2AsObject);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1AsObject[key], obj2AsObject[key])) return false;
  }
  return true;
}

const editUserFormDefaultValues = {
  firstName: '',
  lastName: '',
  firstNameKana: '',
  lastNameKana: '',
  birthday: new Date('2000-01-01'),
  phoneNumber: '',
  mailAddress: '',
  notificationReceiver: NotificationReceiver.SMS,
  twoFactorAuthReceiver: TwoFactorAuthenticationReceiver.SMS,
};

export function UserEdit() {
  const ctx = useContext(AppContext);
  const userId = ctx.userId;
  const isGlobal = ctx.isGlobal;
  const currentLang = ctx.currentLang;

  const [data, loading, err, refresh] = useGetUser(userId);
  const EditUserFormSchema = useEditUserFormSchema(
    data?.user.phone?.value || '',
    isGlobal,
    currentLang
  );
  const [updateUser, updateLoading, updateErr] = useUpdateUser();
  const { isLoading: deleting, deleteLineAccountLink } = useDeleteLineAccountLink();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<EditUserFormSchemaType>({
    resolver: zodResolver(EditUserFormSchema),
    shouldFocusError: true,
    defaultValues: editUserFormDefaultValues,
  });
  const [modalMessage, setModalMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [countryCode, setCountryCode] = useState<Country>(isGlobal ? 'US' : 'JP');
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState<String>('');
  const [showLineUnlinkModal, setShowLineUnlinkModal] = useState(false);
  const [changedToMailModal, setChangedToMailModal] = useState(false);

  const { t: tUserEditDelete } = useTranslationWithLang('userEditDelete');
  const { t: tCommons } = useTranslationWithLang('commons');

  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  useEffect(() => {
    if (err != null) {
      console.log(err);
      setModalMessage(tUserEditDelete('TU006_031'));
      setShowModal(true);
    }
    if (data != null) {
      console.log(data);
      revision = data.user_info_revision;
      const user = data.user;
      const phoneNumber = parsePhoneNumberFromString(user.phone?.value || '');
      setValue('firstName', user.firstName);
      setValue('lastName', user.lastName);
      setValue('firstNameKana', user.firstNameKana);
      setValue('lastNameKana', user.lastNameKana);
      setValue('birthday', user.birthDay);
      setValue('phoneNumber', user.phone?.value || '');
      setValue('mailAddress', user.mailAddress);
      setValue('notificationReceiver', user.notificationReceiver);
      setValue('twoFactorAuthReceiver', user.twoFactorAuthReceiver);
      // 電話番号から国コードを取得
      if (phoneNumber && phoneNumber.country) {
        setCountryCode(phoneNumber.country);
      } else {
        setCountryCode('US' as Country);
      }
      // 表示用電話番号
      setDisplayPhoneNumber(
        phoneNumber
          ? !isGlobal
            ? phoneNumber.number.replace(
                new RegExp(`^\\+${getCountryCallingCode(phoneNumber.country as Country)}`),
                '0'
              )
            : phoneNumber.number
          : ''
      );
    }
  }, [data, err]);

  const watchedFields = useWatch({
    control,
    name: [
      'firstName',
      'lastName',
      'firstNameKana',
      'lastNameKana',
      'birthday',
      'phoneNumber',
      'mailAddress',
      'notificationReceiver',
      'twoFactorAuthReceiver',
    ],
  });
  useEffect(() => {
    if (data == null) return;
    const currentUser = {
      firstName: watchedFields[0],
      lastName: watchedFields[1],
      firstNameKana: watchedFields[2],
      lastNameKana: watchedFields[3],
      birthday: watchedFields[4],
      phoneNumber: watchedFields[5],
      mailAddress: watchedFields[6],
      notificationReceiver: watchedFields[7],
      twoFactorAuthReceiver: watchedFields[8],
    };
    const originalUser = {
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      firstNameKana: data.user.firstNameKana,
      lastNameKana: data.user.lastNameKana,
      birthday: data.user.birthDay,
      phoneNumber: data.user.phone?.value || '',
      mailAddress: data.user.mailAddress,
      notificationReceiver: data.user.notificationReceiver,
      twoFactorAuthReceiver: data.user.twoFactorAuthReceiver,
    };

    setIsFormChanged(!deepEqual(currentUser, originalUser));
  }, [data, watchedFields]);

  const onSubmit: SubmitHandler<EditUserFormSchemaType> = async (userForm) => {
    if (data != null) {
      const isChangedToMail =
        data.user.notificationReceiver !== NotificationReceiver.MailAddress &&
        userForm.notificationReceiver === NotificationReceiver.MailAddress;

      const updatedUser = new User(
        data.user.id,
        userForm.firstName,
        userForm.lastName,
        userForm.firstNameKana,
        userForm.lastNameKana,
        userForm.notificationReceiver,
        userForm.twoFactorAuthReceiver,
        userForm.birthday,
        userForm.mailAddress,
        new PhoneNumber(userForm.phoneNumber)
      );
      console.log(updatedUser);
      updateUser({
        request: {
          user: updatedUser,
          revision,
        },
        onSuccess: () => {
          if (isChangedToMail) {
            setChangedToMailModal(true);
          } else {
            setModalMessage(tUserEditDelete('TU006_042'));
            setShowModal(true);
          }
          refresh();
        },
      });
    }
  };

  useEffect(() => {
    if (updateErr != null) {
      console.error(updateErr);
      setModalMessage(tUserEditDelete('TU006_032'));
      setShowModal(true);
    }
  }, [updateErr]);
  // LINE
  const handleOpenLineUnlinkModal = useCallback(() => setShowLineUnlinkModal(true), []);
  const handleCloseLineUnlinkModal = useCallback(() => setShowLineUnlinkModal(false), []);
  const handleDeleteLineAccount = useCallback(async () => {
    try {
      const revision = data?.user_info_revision === undefined ? -1 : data.user_info_revision;
      setShowLineUnlinkModal(false);
      await deleteLineAccountLink(userId, revision);
      setModalMessage(tUserEditDelete('TU006_042'));
      setShowModal(true);
      refresh();
    } catch {
      setModalMessage(tUserEditDelete('TU006_028'));
      setShowModal(true);
    }
  }, [userId, data?.user_info_revision]);
  // LINE ID連携状態
  const isLinkedLine = useMemo(() => (data?.user.line_id ? true : false), [data?.user.line_id]);
  // 変更ボタンdisable判定. 条件: フォーム状態が変わっていない || (LINE未連携状態 && 連絡先項目がLINE)
  const disabledChangeButton = useMemo(
    () => !isFormChanged || (!isLinkedLine && watchedFields[7] === NotificationReceiver.LINE),
    [isFormChanged, isLinkedLine, watchedFields[7]]
  );
  return (
    <>
      {(loading || updateLoading || deleting) && <LoadingModal />}
      {showModal && (
        <MessageModal
          message={modalMessage}
          closeLabel={tUserEditDelete('TU006_024')}
          onClose={() => setShowModal(false)}
        />
      )}
      {changedToMailModal && (
        <MessageModal
          message={[
            tUserEditDelete('TU006_042'),
            tCommons('TU000_093'),
            process.env.REACT_APP_MAIL_DOMAIN || '',
          ]}
          closeLabel={tUserEditDelete('TU006_024')}
          onClose={() => setChangedToMailModal(false)}
        />
      )}
      <LineUnLinkDialog
        open={showLineUnlinkModal}
        onOk={handleDeleteLineAccount}
        onClose={handleCloseLineUnlinkModal}
      />
      <AppHeader />
      <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
        {tUserEditDelete('TU006_001')}
      </div>
      <PageContent>
        <div className="text-[#876a6a]">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Form title={tUserEditDelete('TU006_001')}>
              <LabelSection
                title={tUserEditDelete('TU006_002')}
                value={data?.user.id || ''}
                id="userId"
              />
              {currentLang === 'ja' && (
                <>
                  <div className="flex flex-col gap-4">
                    <LabeledInputField
                      title={tUserEditDelete('TU006_034')}
                      placeholder={tUserEditDelete('TU006_029')}
                      className="w-[25rem]"
                      ariaLabel={tUserEditDelete('TU006_034')}
                      {...register('lastName')}
                      autoComplete="family-name"
                      id="lastName"
                    />
                    <FieldErrorMessages messages={[errors.lastName?.message]} />
                    <LabeledInputField
                      title={tUserEditDelete('TU006_035')}
                      placeholder={tUserEditDelete('TU006_030')}
                      className="w-[25rem]"
                      ariaLabel={tUserEditDelete('TU006_035')}
                      {...register('firstName')}
                      autoComplete="given-name"
                      id="firstName"
                    />
                    <FieldErrorMessages messages={[errors.firstName?.message]} />
                  </div>
                  <div className="flex flex-col gap-4">
                    <LabeledInputField
                      title={tUserEditDelete('TU006_036')}
                      placeholder={'タナカ'}
                      className="w-[25rem]"
                      ariaLabel={tUserEditDelete('TU006_034')}
                      {...register('lastNameKana')}
                      autoComplete="family-name"
                      id="lastNameKana"
                    />
                    <FieldErrorMessages messages={[errors.lastNameKana?.message]} />
                    <LabeledInputField
                      title={tUserEditDelete('TU006_037')}
                      placeholder={'タロウ'}
                      className="w-[25rem]"
                      ariaLabel={tUserEditDelete('TU006_037')}
                      {...register('firstNameKana')}
                      autoComplete="given-name"
                      id="firstNameKana"
                    />
                    <FieldErrorMessages messages={[errors.firstNameKana?.message]} />
                  </div>
                </>
              )}
              {currentLang === 'en' && (
                <>
                  <div className="flex flex-col gap-4">
                    <LabeledInputField
                      title={tUserEditDelete('TU006_035')}
                      placeholder={tUserEditDelete('TU006_030')}
                      className="w-[25rem]"
                      ariaLabel={tUserEditDelete('TU006_035')}
                      {...register('firstName')}
                      autoComplete="given-name"
                      id="firstName"
                    />
                    <FieldErrorMessages messages={[errors.firstName?.message]} />
                    <LabeledInputField
                      title={tUserEditDelete('TU006_034')}
                      placeholder={tUserEditDelete('TU006_029')}
                      className="w-[25rem]"
                      ariaLabel={tUserEditDelete('TU006_034')}
                      {...register('lastName')}
                      autoComplete="family-name"
                      id="lastName"
                    />
                    <FieldErrorMessages messages={[errors.lastName?.message]} />
                  </div>
                </>
              )}
              <div>
                <FormSectionTitle text={tUserEditDelete('TU006_012')} htmlFor="phoneNumber" />
                <div className="flex w-full">
                  <p className="text-[--c-textinput-text] h-[40px] px-2">{displayPhoneNumber}</p>
                </div>
              </div>
              <FieldErrorMessages messages={[errors.phoneNumber?.message]} />
              <div className="flex flex-col">
                <FormSectionTitle text={tUserEditDelete('TU006_005')} htmlFor="birthday" />
                {currentLang === 'en' ? (
                  <p className="text-[--c-text]" id="birthday">
                    {data?.user.birthDay ? data.user.birthDay.getMonth() + 1 : ''}/
                    {data?.user.birthDay?.getDate() ?? ''}/
                    {data?.user.birthDay?.getFullYear() ?? ''}
                  </p>
                ) : (
                  <p className="text-[--c-text]" id="birthday">
                    {data?.user.birthDay?.getFullYear() ?? ''}/
                    {data?.user.birthDay ? data.user.birthDay.getMonth() + 1 : ''}/
                    {data?.user.birthDay?.getDate() ?? ''}
                  </p>
                )}
              </div>
              <FieldErrorMessages messages={[errors.birthday?.message]} />
              <LabeledInputField
                title={tUserEditDelete('TU006_013')}
                placeholder={'example@example.com'}
                className="w-[25rem]"
                ariaLabel={tUserEditDelete('TU006_013')}
                {...register('mailAddress')}
                autoComplete="email"
                id="mailAddress"
              />
              <FieldErrorMessages messages={[errors.mailAddress?.message]} />

              <LabeledRadioButtonGroup
                title={tUserEditDelete('TU006_018')}
                id="twoFactorAuthReceiver"
                values={[
                  ...(!isGlobal
                    ? [
                        {
                          label: tUserEditDelete('TU006_015'),
                          value: TwoFactorAuthenticationReceiver.SMS,
                          defaultChecked:
                            TwoFactorAuthenticationReceiver.SMS ===
                            editUserFormDefaultValues.twoFactorAuthReceiver,
                          ...register('twoFactorAuthReceiver'),
                        },
                      ]
                    : countryCode === 'TW'
                      ? [
                          {
                            label: tUserEditDelete('TU006_015'),
                            value: TwoFactorAuthenticationReceiver.SMS,
                            defaultChecked:
                              TwoFactorAuthenticationReceiver.SMS ===
                              editUserFormDefaultValues.twoFactorAuthReceiver,
                            ...register('twoFactorAuthReceiver'),
                          },
                          {
                            label: tUserEditDelete('TU006_033'),
                            value: TwoFactorAuthenticationReceiver.MailAddress,
                            defaultChecked:
                              TwoFactorAuthenticationReceiver.MailAddress ===
                              editUserFormDefaultValues.twoFactorAuthReceiver,
                            ...register('twoFactorAuthReceiver'),
                          },
                        ]
                      : [
                          {
                            label: tUserEditDelete('TU006_017'), // WhatsApp
                            value: TwoFactorAuthenticationReceiver.WhatsApp,
                            defaultChecked:
                              TwoFactorAuthenticationReceiver.WhatsApp ===
                              editUserFormDefaultValues.twoFactorAuthReceiver,
                            ...register('twoFactorAuthReceiver'),
                          },
                        ]),
                ]}
              />
              <FieldErrorMessages messages={[errors.twoFactorAuthReceiver?.message]} />

              <LabeledRadioButtonGroup
                title={tUserEditDelete('TU006_014')}
                id="notificationReceiver"
                values={[
                  ...(!isGlobal || countryCode === 'TW'
                    ? [
                        {
                          label: tUserEditDelete('TU006_015'),
                          value: NotificationReceiver.SMS,
                          defaultChecked:
                            NotificationReceiver.SMS ===
                            editUserFormDefaultValues.notificationReceiver,
                          ...register('notificationReceiver'),
                        },
                      ]
                    : [
                        {
                          label: tUserEditDelete('TU006_017'),
                          value: NotificationReceiver.WhatsApp,
                          defaultChecked:
                            NotificationReceiver.WhatsApp ===
                            editUserFormDefaultValues.notificationReceiver,
                          ...register('notificationReceiver'),
                        },
                      ]),
                  {
                    label: tUserEditDelete('TU006_033'),
                    value: NotificationReceiver.MailAddress,
                    defaultChecked:
                      NotificationReceiver.MailAddress ===
                      editUserFormDefaultValues.notificationReceiver,
                    ...register('notificationReceiver'),
                  },
                  ...(!isGlobal
                    ? [
                        {
                          label: tUserEditDelete('TU006_016'),
                          value: NotificationReceiver.LINE,
                          defaultChecked:
                            NotificationReceiver.LINE ===
                            editUserFormDefaultValues.notificationReceiver,
                          ...register('notificationReceiver'),
                        },
                      ]
                    : []),
                ]}
              />
              <FieldErrorMessages messages={[errors.notificationReceiver?.message]} />

              {watchedFields[7] === NotificationReceiver.LINE && (
                <LabelLineAddFriendField
                  title={tUserEditDelete('TU006_043')}
                  description={tUserEditDelete('TU006_044')}
                  unlinkDescription={tUserEditDelete('TU006_045')}
                  linkStatus={tUserEditDelete('TU006_046')}
                  linkedText={tUserEditDelete('TU006_047')}
                  notLinkedText={tUserEditDelete('TU006_048')}
                  unlinkAnchorText={tUserEditDelete('TU006_049')}
                  isLinkedLine={isLinkedLine}
                  onOpenModal={handleOpenLineUnlinkModal}
                />
              )}

              <div className={'h-[20px]'} />
              <div className={'flex justify-center items-center'}>
                <TransitionButton
                  className="mr-6 w-[9rem]"
                  style={{ paddingTop: '0.9rem', paddingBottom: '0.9rem' }}
                />
                <Button
                  label={tUserEditDelete('TU006_019')}
                  type="submit"
                  className="w-[9rem]"
                  disabled={disabledChangeButton}
                />
              </div>
            </Form>
          </form>
        </div>
      </PageContent>
      <AppFooter />
    </>
  );
}
